import type { Address } from "viem"
import BigNumber from "bignumber.js"

import type { ThickV2BaseAddresses, ThickV2TickSpacing, ThickV2TickSpacingInfo } from "./types"

/**
 * Thick-v2 deployment addresses on Base (chainId 8453).
 */
export const THICK_V2_BASE_CHAIN_ID = 8453

export const THICK_V2_BASE_ADDRESSES: ThickV2BaseAddresses = {
  v3CoreFactory: "0x7ca1dccfb4f49564b8f13e18a67747fd428f1c40",
  multicall2: "0x9b500c0a544b870d9c4c441147dcaef599e542e7",
  proxyAdmin: "0x30f45defbbce0030ee14f9e08d36102115e5ce3b",
  tickLens: "0x547db6759b0af9e050855106993e3cefc16e937e",
  nfPositionManager: "0x79c26d7ac184bb9c453b996475f484668e516de5",
  quoterV2: "0xc701e92db1ef14ab64f66f18536b1e07678db678",
  swapRouter02: "0xe4ba08712c404042b8eefc3fdf3b603c977500df",
}

/**
 * GraphQL endpoint path used for all thick-v2 reads. The full URL is
 * `${proxyBaseUrl}${THICK_V2_GRAPHQL_ENDPOINT}` where `proxyBaseUrl` is the
 * branch-resolved scale-proxy base. The endpoint path itself is owned by the
 * domain (no constructor injection of the path).
 */
export const THICK_V2_GRAPHQL_ENDPOINT = "/v1/manual-cl/thick-v2/graphql"

/**
 * Canonical tick-spacing list for thick-v2 on Base. Mirrors the enabled
 * tickSpacing values in the factory constructor.
 */
export const THICK_V2_TICK_SPACINGS: readonly ThickV2TickSpacingInfo[] = [
  { key: 1, label: "1", description: "Finest spacing" },
  { key: 8, label: "8", description: "Balanced spacing" },
  { key: 64, label: "64", description: "Wide spacing" },
] as const

export function getThickV2TickSpacingInfo(
  tickSpacing: ThickV2TickSpacing,
): ThickV2TickSpacingInfo {
  const found = THICK_V2_TICK_SPACINGS.find((t) => t.key === tickSpacing)
  if (!found) {
    throw new Error(`Unknown thick-v2 tick spacing: ${tickSpacing}`)
  }
  return found
}

export function asThickV2TickSpacing(tickSpacing: number): ThickV2TickSpacing {
  if (tickSpacing === 1 || tickSpacing === 8 || tickSpacing === 64) {
    return tickSpacing
  }
  throw new Error(`Unsupported thick-v2 tick spacing: ${tickSpacing}`)
}

/**
 * NFP manager address kept as a top-level export for legacy call-sites that
 * predate `THICK_V2_BASE_ADDRESSES`. The value mirrors
 * `THICK_V2_BASE_ADDRESSES.nfPositionManager`.
 */
export const THICK_V2_NFP_MANAGER_ADDRESS: Address = THICK_V2_BASE_ADDRESSES.nfPositionManager

/**
 * Uniswap v3 tick-math constants. Also exported from `manual-cl-core` — kept
 * here for backwards compatibility with call-sites that imported them from
 * the domain folder.
 */
export const TICK_SIZE = 1.0001
export const MIN_TICK = -887272
export const MAX_TICK = 887272

export type TickSpacingOption = {
  key: ThickV2TickSpacing
  value: string
  description: string
}

export const getSupportedTickSpacingsForChain = (chainId: number): Array<TickSpacingOption> => {
  if (chainId === THICK_V2_BASE_CHAIN_ID) {
    return THICK_V2_TICK_SPACINGS.map((spacing) => ({
      key: spacing.key,
      value: spacing.label,
      description: spacing.description,
    }))
  }
  return []
}

/**
 * Used in liquidity-amount math. BigNumber variant of Uniswap's Q96/Q192
 * JSBI constants.
 * @source https://github.com/Uniswap/sdks/blob/main/sdks/v3-sdk/src/internalConstants.ts#L8-L10
 */
export const Q96 = BigNumber(2).pow(96)
export const Q192 = Q96.pow(2)
